import { Box, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { CoreModal, coreToast } from "@verity/components/core";
import { Dispatch, SetStateAction } from "react";
import { Trans } from "react-i18next";
import apiClient from "../../../../../../helpers/api-client";
import i18n from "../../../../../../i18n";
import styles from "./DeleteInterviewModal.module.css";

function formatDateTime(date: Date): string {
	const time = new Intl.DateTimeFormat("en-US", {
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	}).format(date);

	const day = new Intl.DateTimeFormat("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
	}).format(date);

	return `${time} on ${day}`;
}

type DeleteInterviewModalProps = {
	open: boolean;
	setOpen: Dispatch<SetStateAction<boolean>>;
	assessmentId: number;
	candidateName: string;
	startTime: Date;
};

export default function DeleteInterviewModal({
	open,
	setOpen,
	assessmentId,
	candidateName,
	startTime,
}: DeleteInterviewModalProps) {
	const queryClient = useQueryClient();

	async function handleDelete() {
		try {
			if (!(await apiClient.lmsInfo.deleteAssessment(assessmentId, false)).success) {
				throw Error(`Failed to delete the assessment.`);
			}

			await queryClient.invalidateQueries({ queryKey: ["institute-interviews"] });
			return coreToast.success(i18n.t("portal.components.assessmentPageTSX.deleteAssessment.successToast"), {
				size: "large",
			});
		} catch (err) {
			coreToast.error(i18n.t("portal.components.assessmentPageTSX.deleteAssessment.deleteError"), {
				autoClose: false,
				size: "large",
			});
			let message = "Failed to delete the assessment.";
			if (err instanceof Error) {
				message += " " + err.message;
			}
			throw Error(message);
		}
	}

	return (
		<CoreModal
			width="60%"
			maxWidth="720px"
			modalMargin="57px 0px 0px 0px"
			open={open}
			onClose={() => setOpen(false)}
			title={i18n.t("candidate.instructorDashboard.deleteModal.title")}
			modalVerticalPosition="top"
			keepTopBorderRadius
			buttons={[
				{
					text: i18n.t("candidate.instructorDashboard.deleteModal.button.cancel"),
					color: "secondary",
					onClick: () => setOpen(false),
				},
				{
					text: i18n.t("candidate.instructorDashboard.deleteModal.button.delete"),
					color: "cancel",
					onClick: handleDelete,
				},
			]}
		>
			<Box>
				<Typography className={styles.deleteModal}>
					<Trans
						i18nKey="candidate.instructorDashboard.deleteModal.description"
						values={{ candidateName, time: formatDateTime(startTime) }}
						components={{ strong: <strong /> }}
					/>
				</Typography>
			</Box>
		</CoreModal>
	);
}
